/**
 * PDF Component - Converted to use @react-pdf/renderer
 *
 * This component has been converted from the old Canvas-based PDF generation
 * to use the new @react-pdf/renderer library, following the same pattern as
 * user-service-orders-pdf.tsx.
 *
 * Changes made:
 * - Replaced jsPDF + Canvas with @react-pdf/renderer
 * - Created PericiaPDFDocument component using Document/Page/View/Text
 * - Replaced Canvas drawing functions with react-pdf styling
 * - Used PDFDownloadLink instead of manual PDF generation
 * - Commented out old Canvas-based implementation for future reference
 *
 * Note: @react-pdf/renderer package needs to be installed
 * Run: npm install @react-pdf/renderer
 */

import React from "react";
import { Document, Page, Text, View, StyleSheet, Font, Image } from "@react-pdf/renderer";
import { Button } from "@mui/material";
import { PDFDownloadLink } from "@react-pdf/renderer";
import { removeWhiteSpaces } from "../../utils/helpers/strings";
import { formatCurrency } from "../../shared/lib/numbers";
import { PericiaCostumer } from "../../entities/costumer/model/costumer";
import { Car } from "../../entities/car/model/car";
import { Locale, getTranslation } from "../../shared/locale/locale";
import { CarPart } from "../../entities/pericia";

const carroImg = require("../../assets/pericia_4.png");
const nuovautoImg = require("../../assets/nuovauto.png");

Font.register({
  family: "Helvetica",
  src: "https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.53/fonts/Helvetica.ttf"
});

interface PDFGeneratorProps {
  disabled: boolean;
  withCostumerPrice: boolean;
  withTableHours?: boolean;
  props: Props;
}

interface Props {
  costumer: PericiaCostumer;
  car: Car;
  carParts: CarPart[];
  date: Date;
  finished: boolean;
  shouldUnmount: boolean;
  unmountPrice: number;
  costumerPrice: number;
  addtionalCost: number;
  totalPrice: number;
  insurancePrice: number;
  addtionalCostDescription: string;
  totalHours: number;
}

const CAR_PARTS_COORDINATES = {
  Parafango_ad: { x: 105, y: 360, degrees: 0, shouldSplit: true, drawLineToX: 130, longTextY: 0 },
  Parafango_pd: { x: 105, y: 120, degrees: 0, shouldSplit: true, drawLineToX: 140, longTextY: 0 },
  Parafango_ps: { x: 420, y: 120, degrees: 0, shouldSplit: true, drawLineToX: 385, longTextY: 0 },
  Parafango_as: { x: 420, y: 360, degrees: 0, shouldSplit: true, drawLineToX: 395, longTextY: 0 },
  Porta_ad: { x: 110, y: 280, degrees: 0, shouldSplit: true, drawLineToX: 0, longTextY: 0 },
  Porta_pd: { x: 110, y: 185, degrees: 0, shouldSplit: true, drawLineToX: 0, longTextY: 0 },
  Porta_as: { x: 410, y: 280, degrees: 0, shouldSplit: true, drawLineToX: 0, longTextY: 0 },
  Porta_ps: { x: 410, y: 185, degrees: 0, shouldSplit: true, drawLineToX: 0, longTextY: 0 },
  Cofano: { x: 256, y: 395, degrees: 270, shouldSplit: false, drawLineToX: 0, longTextY: 0 },
  Tetto: { x: 256, y: 200, degrees: 270, shouldSplit: false, drawLineToX: 0, longTextY: 0 },
  Piantone_d: { x: 200, y: 160, degrees: 270, shouldSplit: false, drawLineToX: 0, longTextY: 150 },
  Piantone_s: { x: 313, y: 160, degrees: 270, shouldSplit: false, drawLineToX: 0, longTextY: 150 },
  Sportello_s: { x: 265, y: 80, degrees: 0, shouldSplit: false, drawLineToX: 0, longTextY: 0 },
  Sportello_i: { x: 265, y: 0, degrees: 0, shouldSplit: false, drawLineToX: 0, longTextY: 0 }
};

// New PDF styles using react-pdf
const styles = StyleSheet.create({
  page: {
    fontFamily: "Helvetica",
    fontSize: 12,
    padding: 15,
    backgroundColor: "#ffffff",
    color: "#000",
    lineHeight: 1.4
  },
  pageContent: {
    borderWidth: 2,
    borderColor: "#000",
    borderStyle: "solid",
    padding: 15,
    minHeight: "100%"
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 15,
    borderBottomWidth: 1,
    borderColor: "#000",
    paddingBottom: 10
  },
  headerLeft: {
    flex: 1
  },
  headerRight: {
    alignItems: "flex-end"
  },
  nuovautoLogo: {
    width: 120,
    height: 40
  },
  carImage: {
    width: 500,
    height: 400,
    alignSelf: "center",
    marginVertical: 50,
    marginTop: 50,
    transform: "rotate(270deg)"
  },
  identificationSection: {
    marginBottom: 15,
    fontSize: 10
  },
  boldLabel: {
    fontWeight: "bold"
  },
  flexRow: {
    flexDirection: "row",
    justifyContent: "space-between"
  },
  carPartsContainer: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0
  },
  carPartText: {
    position: "absolute",
    fontSize: 10,
    color: "#333333",
    fontWeight: "bold",
    transform: "rotate(270deg)"
  },
  carPartTextCentered: {
    position: "absolute",
    fontSize: 10,
    color: "#333333",
    fontWeight: "bold",
    width: 80, // Fixed width container
    textAlign: "center",
    transformOrigin: "center top"
  },
  connectionLine: {
    position: "absolute",
    height: 1,
    backgroundColor: "#333333",
    borderTopWidth: 1,
    borderTopColor: "#333333"
  },
  arrowMarker: {
    position: "absolute",
    fontSize: 8,
    color: "#333333",
    fontWeight: "bold",
    textAlign: "center",
    width: 8,
    height: 8
  },
  legend: {
    position: "absolute",
    bottom: 150,
    right: 20,
    fontSize: 8
  },
  priceSection: {
    marginTop: 5,
    padding: 5,
    borderWidth: 1,
    borderColor: "#000",
    fontSize: 8
  },
  priceItem: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: -2
  },
  totalPrice: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 10,
    paddingTop: 10,
    borderTopWidth: 1,
    borderColor: "#000",
    fontWeight: "bold"
  }
});

// PDF Document Component
const PericiaPDFDocument: React.FC<{ props: Props; text: Locale; withCostumerPrice: boolean; withTableHours: boolean }> = ({
  props,
  text,
  withCostumerPrice,
  withTableHours
}) => {
  const {
    carParts,
    costumer,
    car,
    date,
    finished,
    shouldUnmount,
    unmountPrice,
    costumerPrice,
    addtionalCost,
    totalPrice,
    insurancePrice,
    addtionalCostDescription,
    totalHours
  } = props;
  console.log("VEJA: ", addtionalCostDescription);

  const { name: customerName } = costumer;
  const { plate, chassisNumber, brand, model, color, insuranceName } = car;
  let priceText = text.pericia_pdf.cost_description.repair_cost;

  let finalPrice = totalPrice;
  if (insurancePrice > 0) {
    finalPrice = insurancePrice;
    priceText = text.pericia_pdf.cost_description.insurance_price;
  }
  if (Number(costumerPrice) > 0) {
    finalPrice = Number(costumerPrice);
    priceText = text.pericia_pdf.cost_description.costumer_price;
  }

  // Add additional costs to the final price
  if (unmountPrice > 0) {
    finalPrice += unmountPrice;
  }
  if (addtionalCost > 0) {
    finalPrice += addtionalCost;
  }

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.pageContent}>
          {/* Header */}
          <View style={styles.header}>
            <View style={styles.headerLeft}>
              <Text>
                <Text style={styles.boldLabel}>{`${text.pericia_pdf.header.client_name}: `}</Text> {customerName}
              </Text>
              <Text>
                <Text style={styles.boldLabel}>{`${text.pericia_pdf.header.date}: `}</Text>
                {date.toLocaleDateString("pt-br")}
              </Text>
            </View>
            <View style={styles.headerRight}>
              <Image src={nuovautoImg} style={styles.nuovautoLogo} />
            </View>
          </View>

          {/* Vehicle Information */}
          <View style={styles.identificationSection}>
            <View style={styles.flexRow}>
              <Text>
                <Text style={styles.boldLabel}>{text.pericia_pdf.header.brand}:</Text> {brand}
              </Text>
              <Text>
                <Text style={styles.boldLabel}>{text.pericia_pdf.header.chassis}:</Text> {chassisNumber || ""}
              </Text>
            </View>
            <View style={styles.flexRow}>
              <Text>
                <Text style={styles.boldLabel}>{text.pericia_pdf.header.model}:</Text> {model}
              </Text>
              <Text>
                <Text style={styles.boldLabel}>{text.pericia_pdf.header.color}:</Text> {color || ""}
              </Text>
            </View>
            <View style={styles.flexRow}>
              <Text>
                <Text style={styles.boldLabel}>{text.pericia_pdf.header.plate}:</Text> {plate}
              </Text>
              <Text>
                <Text style={styles.boldLabel}>{text.pericia_pdf.header.insurance_name}:</Text> {insuranceName || ""}
              </Text>
            </View>

            {finished && (
              <Text>
                <Text style={styles.boldLabel}>{text.pericia_pdf.header.finished}</Text>
              </Text>
            )}

            {shouldUnmount && (
              <Text>
                <Text style={styles.boldLabel}>{text.pericia_pdf.header.unmount}</Text>
              </Text>
            )}

            {withTableHours && (
              <Text>
                <Text style={styles.boldLabel}>Ore:</Text> {totalHours / 10}
              </Text>
            )}
          </View>

          {/* Car Image with parts overlay */}
          <View style={{ position: "relative" }}>
            <Image src={carroImg} style={styles.carImage} />

            {/* Car Parts - Centered around X coordinate */}
            {carParts.map((part, index) => {
              const coordinates = CAR_PARTS_COORDINATES[part.name as keyof typeof CAR_PARTS_COORDINATES];
              const messageText =
                part.note.smashes.length === 0 && part.note.details.length === 0
                  ? ""
                  : `${part.note.smashes} ${coordinates.shouldSplit ? "\n" : ""}${part.note.details.trim()}`;

              // Determine Y coordinate based on text length and longTextY availability
              const yCoordinate = coordinates.longTextY > 0 && messageText.length > 5 ? coordinates.longTextY : coordinates.y;

              return (
                <React.Fragment key={index}>
                  {/* Car Part Text */}
                  <View
                    style={[
                      styles.carPartTextCentered,
                      {
                        top: yCoordinate,
                        left: coordinates.x - 40, // Subtract half of container width (80/2 = 40)
                        transform: `rotate(${coordinates.degrees}deg)`
                      }
                    ]}
                  >
                    <Text>{messageText}</Text>
                  </View>

                  {/* Connection Line - if drawLineToX is specified and there is messageText */}
                  {coordinates.drawLineToX > 0 && messageText && (
                    <>
                      <View
                        style={[
                          styles.connectionLine,
                          {
                            top: yCoordinate + 15, // Position below the text
                            left: Math.min(coordinates.x, coordinates.drawLineToX),
                            width: Math.abs(coordinates.x - coordinates.drawLineToX)
                          }
                        ]}
                      />
                      {/* X marker at the end of the line */}
                      <View
                        style={[
                          styles.arrowMarker,
                          {
                            top: yCoordinate + 11, // Position to align with line
                            left: coordinates.drawLineToX - 4 // Center X on the target point
                          }
                        ]}
                      >
                        <Text>X</Text>
                      </View>
                    </>
                  )}
                </React.Fragment>
              );
            })}
          </View>

          {/* Legend */}
          <View style={styles.legend}>
            <Text>X: {text.pericia_pdf.legends.additional_repair}</Text>
            <Text>C: {text.pericia_pdf.legends.glue}</Text>
            <Text>V: {text.pericia_pdf.legends.paint}</Text>
            <Text>AL: {text.pericia_pdf.legends.aluminium}</Text>
            <Text>SOST: {text.pericia_pdf.legends.change}</Text>
          </View>

          {/* Price Section */}
          {withCostumerPrice && (
            <View style={styles.priceSection} break={false}>
              <View style={styles.priceItem}>
                <Text>{priceText}</Text>
                <Text>{formatCurrency(Number(totalPrice))}</Text>
              </View>

              {unmountPrice > 0 && (
                <View style={styles.priceItem}>
                  <Text>{text.pericia_pdf.cost_description.unmount}</Text>
                  <Text>{formatCurrency(unmountPrice)}</Text>
                </View>
              )}

              {addtionalCost > 0 && (
                <View style={styles.priceItem}>
                  <Text>{text.pericia_pdf.cost_description.additional_repair}</Text>
                  <Text>{formatCurrency(addtionalCost)}</Text>
                </View>
              )}

              <View style={styles.totalPrice}>
                <Text>{text.pericia_pdf.cost_description.total}</Text>
                <Text>{formatCurrency(finalPrice)}</Text>
              </View>

              {addtionalCostDescription.length > 0 && (
                <View>
                  <Text>{addtionalCostDescription && `Ammaccatura: ${addtionalCostDescription}`}</Text>
                </View>
              )}
            </View>
          )}
        </View>
      </Page>
    </Document>
  );
};

const PDFGenerator: React.FC<PDFGeneratorProps> = ({ disabled, withCostumerPrice, withTableHours = false, props }) => {
  const { costumer } = props;
  const text = getTranslation(costumer.language || "ita");
  const { name: customerName } = costumer;
  const { chassisNumber } = props.car;

  let btnText = "";
  if (withCostumerPrice) {
    btnText = "Gerar PDF com preço";
  } else if (withTableHours) {
    btnText = "Gerar PDF com horas";
  } else {
    btnText = "Gerar PDF";
  }

  const fileNameSuffix = withCostumerPrice ? "-prezzo" : withTableHours ? "-ore" : "";
  let chassisNumberValue = chassisNumber.length < 2 ? "assente" : chassisNumber;
  const fileName = `${removeWhiteSpaces(customerName)}-${chassisNumberValue}-${props.date.toLocaleDateString(
    "pt-br"
  )}${fileNameSuffix}.pdf`;

  return (
    <>
      <PDFDownloadLink
        document={<PericiaPDFDocument props={props} text={text} withCostumerPrice={withCostumerPrice} withTableHours={withTableHours} />}
        fileName={fileName}
      >
        {({ loading }: { loading: boolean }) => (
          <Button fullWidth variant="contained" sx={{ mb: 1 }} disabled={disabled || loading}>
            {loading ? "Gerando PDF..." : btnText}
          </Button>
        )}
      </PDFDownloadLink>
    </>
  );
};

export default PDFGenerator;
